import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";

import { QuoteRepository } from "../../quote.repository";
import { quoteLocationType } from "./quote-location-type.const";
import { Quote } from "../../quote.type";
import { QuoteLocation } from "../../models/quote-location.model";
import { Location } from "../../../location/location.model";
import { NotifyService } from "../../../notify/notify.service";
import { TOAST } from "../../../notify/toast/toast-type.enum";
import { eventType } from "../../../notify/event.const";
import { BackendErrorPipe } from "../../../shared/backend/backend-error.pipe";
import { EventService } from "../../../shared/event/event.service";
import { PlaceDetails } from "app/shared/google-maps/place-autocomplete.service";
import { Bind } from "primeng/bind";
import { ButtonDirective } from "primeng/button";
import { Checkbox } from "primeng/checkbox";
import { FormsModule } from "@angular/forms";
import { AutoComplete } from "primeng/autocomplete";
import { LocationIconComponent } from "../../../ui/location-icon/location-icon.component";
import { Tooltip } from "primeng/tooltip";
import { LocationOrGroupSelectComponent } from "../../../ui/dropdown/location-or-group-select/location-or-group-select.component";
import { PlaceAutocompleteComponent } from "../../../ui/dropdown/place-autocomplete/place-autocomplete.component";
import { LocationGroup } from "../../../location-group/location-group.model";
import { LocationGroupRepository } from "../../../location-group/location-group.repository";
import { Observable, from } from "rxjs";
import { concatMap, switchMap } from "rxjs/operators";

@Component({
  selector: "app-quote-chips",
  templateUrl: "./quote-chips.component.html",
  styleUrls: ["./quote-chips.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    Bind,
    Checkbox,
    FormsModule,
    AutoComplete,
    LocationIconComponent,
    Tooltip,
    LocationOrGroupSelectComponent,
    PlaceAutocompleteComponent,
  ],
})
export class QuoteChipsComponent implements OnInit {
  private _eref = inject(ElementRef);
  private quoteRepository = inject(QuoteRepository);
  private locationGroupRepository = inject(LocationGroupRepository);
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);

  public types = quoteLocationType;

  public readonly type = input<string>(undefined);
  public readonly locationKindFilter = input("");
  public readonly locationKindFilterOptions = input([]);
  public readonly quote = input<Quote>(undefined);
  @Input() public hasLocation: boolean;

  readonly hasLocationChange = output<boolean>();
  readonly locationChange = output<{
    type: string;
    locations: QuoteLocation[];
  }>();
  readonly locationKindFilterChange = output<string>();

  public readonly locationSelect = viewChild(LocationOrGroupSelectComponent);
  public loading: boolean = false;

  // Set while the picked place's details are in flight, so the leg cannot be
  // saved before its coordinates and address have landed.
  public placePending = false;

  // TODO: This should be converted to Reactive Form, or QuoteLocation model
  public currentEditedLocationId: number = null;
  public inlandHaulage = "";
  private location: Location;
  private locationGroup: LocationGroup;
  private latitude: number;
  private longitude: number;
  private postalCode: string;
  private country: string;

  private _locations = [];
  private expanded = true;

  ngOnInit() {
    this.expanded = this.locations.length ? false : true;
  }

  public onPlaceSelect(place: PlaceDetails): void {
    this.inlandHaulage = place.label;
    this.latitude = place.latitude;
    this.longitude = place.longitude;

    if (place.country) {
      this.country = place.country;
    }

    if (place.postalCode) {
      this.postalCode = place.postalCode;
    }
  }

  @Input()
  set locations(locations: QuoteLocation[]) {
    if (locations !== null) {
      this._locations = locations;
    }
  }

  get locations(): QuoteLocation[] {
    return this._locations;
  }

  public addElement() {
    if (this.locationGroup) {
      this.addLocationGroup();

      return;
    }

    if (!this.location) {
      this.notifyService.toast(TOAST.ERROR, "Please select location");
    }

    if (this.location) {
      this.loading = true;
      this.emitQuoteUpdating();
      this.addQuoteLocation(this.location).subscribe({
        next: (quoteLocation): void => {
          this.locations.push(quoteLocation);
          this.onLocationsAdded("Location has been added");
        },
        error: (err): void => {
          this.loading = false;
          this.notifyService.toast(
            TOAST.ERROR,
            "Adding location error",
            new BackendErrorPipe().transform(err, "string"),
          );
        },
      });
    } else {
      this.notifyService.toast(TOAST.ERROR, "Please select location");
    }
  }

  public removeLocationPressed(item: QuoteLocation) {
    this.locations = this.locations.filter((loc) => loc !== item);
    this.removeElement({ value: item });
  }

  public removeElement(event) {
    const endpoint = this.isOrigin() ? "deleteOrigin" : "deleteDestination";

    this.emitQuoteUpdating();
    this.quoteRepository[endpoint](
      this.quote().getId(),
      event.value.getId(),
    ).subscribe({
      next: (response): void => {
        this.looseFocusOnEditedLocation();
        this.emitQuoteUpdated();
        this.notifyService.toast(TOAST.SUCCESS, "Location has been removed");
        this.locationChange.emit({
          type: this.type(),
          locations: this.locations,
        });
      },
      error: (err): void => {
        this.notifyService.toast(TOAST.ERROR, "Removing location error");
      },
    });
  }

  public editLocation(location: QuoteLocation): void {
    this.currentEditedLocationId = location.getId();
    this.location = location.getLocation();
    this.locationGroup = null;
    this.inlandHaulage = location.getInlandHaulage();
    this.latitude = location.getLatitude();
    this.longitude = location.getLongitude();
    this.postalCode = location.getPostalCode();
    this.country = location.getCountry();
  }

  public saveInland() {
    const endpoint = this.isOrigin() ? "patchOrigin" : "patchDestination";

    if (!this.inlandHaulage) {
      this.postalCode = null;
      this.latitude = null;
      this.longitude = null;
      this.country = null;
    }

    this.emitQuoteUpdating();
    this.quoteRepository[endpoint](
      this.quote().getId(),
      this.currentEditedLocationId,
      this.location,
      this.inlandHaulage,
      this.postalCode,
      this.latitude,
      this.longitude,
      this.country,
    ).subscribe({
      next: (quoteLocation): void => {
        this.looseFocusOnEditedLocation();
        this.updateOnList(quoteLocation);
        this.emitQuoteUpdated();
        this.locationChange.emit({
          type: this.type(),
          locations: this.locations,
        });
        this.notifyService.toast(TOAST.SUCCESS, "Location has been added");
      },
      error: (err): void => {
        this.notifyService.toast(TOAST.ERROR, "Adding location error");
      },
    });
  }

  public looseFocusOnEditedLocation() {
    this.currentEditedLocationId = null;
    this.location = null;
    this.locationGroup = null;
    this.inlandHaulage = null;
    this.latitude = null;
    this.longitude = null;
    this.postalCode = null;
    this.country = null;
  }

  @HostListener("document: mousedown", ["$event"]) onMouseDown(event) {
    if (
      !this._eref.nativeElement.contains(event.target) &&
      !this.hasSomeParentTheClass(event.target, "p-select-overlay") &&
      !this.hasSomeParentTheClass(event.target, "ui-dropdown-item") &&
      !this.hasSomeParentTheClass(event.target, "ui-dropdown-filter") &&
      !this.hasSomeParentTheClass(event.target, "ui-autocomplete-list-item") &&
      !!this.locations &&
      this.locations.length
    ) {
      this.expanded = false;
      this.looseFocusOnEditedLocation();
    } else if (
      !this.expanded &&
      this._eref.nativeElement.contains(event.target)
    ) {
      this.expanded = true;
      this.emitLocationKindFromFirstLocationOnList();
    }
  }

  public isExpanded(): boolean {
    return this.expanded || !this.locations.length;
  }

  public onLocationChange(event: Location | LocationGroup) {
    if (event instanceof LocationGroup) {
      this.location = null;
      this.locationGroup = event;
    } else {
      this.location = event;
      this.locationGroup = null;
    }
  }

  private addLocationGroup(): void {
    let added = 0;
    let skippedInactive = false;

    this.loading = true;
    this.emitQuoteUpdating();
    this.locationGroupRepository
      .get(this.locationGroup.getId())
      .pipe(
        switchMap((locationGroup: LocationGroup) => {
          const missing = this.locationsMissingFromList(locationGroup);
          skippedInactive = this.warnAboutInactiveLocations(missing);

          return from(missing.filter((location) => location.active));
        }),
        concatMap((location) => this.addQuoteLocation(location)),
      )
      .subscribe({
        next: (quoteLocation): void => {
          this.locations.push(quoteLocation);
          added++;
        },
        complete: (): void => {
          this.onLocationsAdded(
            this.locationGroupAddedMessage(added, skippedInactive),
          );
        },
        error: (err): void => {
          this.loading = false;
          if (added) {
            this.refreshList();
            this.emitQuoteUpdated();
            this.emitLocationChange();
          }
          this.notifyService.toast(
            TOAST.ERROR,
            "Adding location error",
            new BackendErrorPipe().transform(err, "string"),
          );
        },
      });
  }

  private locationsMissingFromList(locationGroup: LocationGroup): Location[] {
    return locationGroup
      .getLocations()
      .filter((location) => !this.hasAddedLocation(location));
  }

  // The single-location picker refuses these, so the group shortcut skips them.
  private warnAboutInactiveLocations(locations: Location[]): boolean {
    const inactive = locations.filter((location) => !location.active);
    if (inactive.length) {
      this.notifyService.toast(
        TOAST.WARN,
        "Locations in inactive countries skipped",
        inactive.map((location) => location.getName()).join(", "),
      );
    }

    return inactive.length > 0;
  }

  private locationGroupAddedMessage(
    added: number,
    skippedInactive: boolean,
  ): string | null {
    if (added) {
      return "Locations have been added";
    }

    return skippedInactive
      ? null
      : "All locations of the group are already added";
  }

  private addQuoteLocation(location: Location): Observable<QuoteLocation> {
    const endpoint = this.isOrigin() ? "addOrigin" : "addDestination";

    return this.quoteRepository[endpoint](
      this.quote().getId(),
      location.getId(),
      this.inlandHaulage,
      this.postalCode,
      this.latitude,
      this.longitude,
      this.country,
    );
  }

  private onLocationsAdded(message: string | null): void {
    this.loading = false;
    this.looseFocusOnEditedLocation();
    this.refreshList();
    this.inlandHaulage = "";
    this.locationSelect()?.resetTouchedState();
    this.emitQuoteUpdated();
    this.emitLocationChange();
    if (message) {
      this.notifyService.toast(TOAST.SUCCESS, message);
    }
  }

  private emitLocationChange(): void {
    this.locationChange.emit({
      type: this.type(),
      locations: this.locations,
    });
  }

  private updateOnList(locationToChange: QuoteLocation) {
    for (let i = this.locations.length - 1; i >= 0; i--) {
      if (this.locations[i].getId() === locationToChange.getId()) {
        this.locations[i] = locationToChange;
        this.refreshList();
        break;
      }
    }
  }

  private refreshList() {
    this.locations = [...this.locations];
  }

  private hasAddedLocation(location: Location): boolean {
    return this.locations.some((quoteLocation) => {
      if (location && quoteLocation.getLocation()) {
        return quoteLocation.getLocation().getId() === location.getId();
      }
    });
  }

  private emitQuoteUpdating(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdating,
      data: this.quote(),
    });
  }

  private emitQuoteUpdated(): void {
    this.eventService.eventBus.emit({
      event: eventType.quoteUpdated,
      data: this.quote(),
    });
  }

  private isOrigin(): boolean {
    return this.type() === quoteLocationType.origin;
  }

  private hasSomeParentTheClass(element, classname) {
    if (
      typeof element.className === "string" &&
      element.className.split(" ").indexOf(classname) >= 0
    ) {
      return true;
    }

    return (
      element.parentNode &&
      this.hasSomeParentTheClass(element.parentNode, classname)
    );
  }

  private emitLocationKindFromFirstLocationOnList() {
    this.locationKindFilterChange.emit(
      this.locations && this.locations[0] && this.locations[0].getLocation()
        ? this.locations[0].getLocation().getKind()
        : "",
    );
  }
}
